import { useMemo, useState } from 'react';
import { useBalance, useConnection, useReadContract } from 'wagmi';
import { stakingManagerAbi, uniswapV2RouterAbi } from '@syntra/abi';
import { ActionButton } from '../components/tx/ActionButton.tsx';
import { CountUp } from '../components/ui/motion.tsx';
import { Icon } from '../components/ui/Icon.tsx';
import { AmountInput, Card, Notice, Rows, SectionHead, Segmented } from '../components/ui/primitives.tsx';
import { AreaChart, type XY } from '../components/ui/viz.tsx';
import { DEPLOYMENT as D } from '../config/env.ts';
import { useChainNow } from '../hooks/useChainClock.tsx';
import { useIndexer } from '../hooks/useIndexer.ts';
import { usePool } from '../hooks/usePool.ts';
import { usePosition, useStakes } from '../hooks/usePosition.ts';
import { NO_LOCK, useProtocol } from '../hooks/useProtocol.ts';
import { useI18n } from '../i18n/index.tsx';
import { pathFor, priceImpact, withSlippage, type Sym } from '../lib/amm.ts';
import { fmtBps, fmtNum, fmtPct, fmtToken, fmtUsd, parseAmount, toNum, toInput, fmtBalance } from '../lib/format.ts';
import { afterExitFee, aprOf, apyOf } from '../lib/plans.ts';
import { Coin, PoolStacks } from '../components/ui/coins.tsx';
import { TokenSelect } from '../components/ui/TokenSelect.tsx';
import { JoinFirst } from '../components/tx/JoinFirst.tsx';

const ICONS: Record<Sym, string> = { SYN: '/token-syntra.png', USDT: '/token-usdt.svg', BNB: '/token-bnb.svg' };
// SYN and USDT go by their names; BNB by a description, which is translated (swap.nativeCoin)
const NAMES: Record<Exclude<Sym, 'BNB'>, string> = { SYN: 'Syntra', USDT: 'Tether USD' };
const HOUR = 3600;

export interface PriceRow {
  hour: string;
  close: string;
}

/** Closes every `step` seconds from `from` to `to` (unix seconds on the hour). The pool only re-prices on trades, so
 *  an hour without a trade carries the previous close forward; the series starts at the first known close. */
export function hourlyCloses(rows: PriceRow[], from: number, to: number, step = HOUR): XY[] {
  const known = rows.map((r) => ({ h: Number(r.hour), c: toNum(BigInt(r.close)) })).sort((a, b) => a.h - b.h);
  const out: XY[] = [];
  let i = 0;
  let last: number | undefined;
  for (let h = from; h <= to; h += step) {
    while (i < known.length && known[i].h <= h) {
      const k = known[i++];
      if (last === undefined && k.h > from && k.h < h) out.push({ x: k.h, y: k.c }); // start at the first close, not the next step
      last = k.c;
    }
    if (last !== undefined) out.push({ x: h, y: last });
  }
  return out;
}

const RANGES = [
  { key: '24H', hours: 24, step: HOUR },
  { key: '7D', hours: 168, step: 4 * HOUR },
  { key: '30D', hours: 720, step: 12 * HOUR },
] as const;

/** SYN's hourly closes from the indexer as a glowing area chart, with the change over the chosen range. */
function PriceCard() {
  const { t } = useI18n();
  const [range, setRange] = useState<(typeof RANGES)[number]>(RANGES[1]);
  const now = useChainNow(60_000);
  const q = useIndexer<{ points: PriceRow[] }>(`/prices?hours=${range.hours + 2}`, 60_000);
  const end = Math.floor(now / HOUR) * HOUR;
  const points = useMemo(() => hourlyCloses(q.data?.points ?? [], end - range.hours * HOUR, end, range.step), [q.data, end, range]);
  const first = points[0]?.y;
  const last = points[points.length - 1]?.y;
  const change = first && last !== undefined ? last / first - 1 : undefined;
  const fmtX = (x: number) => {
    const d = new Date(x * 1000);
    return range.hours <= 24 ? `${String(d.getHours()).padStart(2, '0')}:00` : `${d.getMonth() + 1}/${d.getDate()}`;
  };
  return (
    <Card className="price-card">
      <div className="card-head">
        <h3>{t('swap.priceChart')}</h3>
        <div className="range-tabs" role="group" aria-label={t('swap.priceRange')}>
          {RANGES.map((r) => (
            <button key={r.key} type="button" aria-pressed={r.key === range.key} onClick={() => setRange(r)}>
              {r.key}
            </button>
          ))}
        </div>
      </div>
      <div className="price-head">
        <Coin token="SYN" size={38} />
        <span className="big num">
          <CountUp value={last} format={(v) => `$${v.toFixed(4)}`} />
        </span>
        {change !== undefined ? (
          <span className={`change ${change > 0 ? 'up' : change < 0 ? 'down' : 'flat'}`}>
            {change > 0 ? '+' : ''}
            {fmtPct(change, 2)} · {range.key}
          </span>
        ) : null}
      </div>
      {points.length >= 2 ? (
        <AreaChart key={range.key} points={points} label={t('swap.priceChart')} formatX={fmtX} formatY={(y) => `$${y.toFixed(4)}`} height={200} />
      ) : (
        <p className="soft" style={{ margin: '24px 0' }}>
          {q.configured ? t('swap.noHistory') : t('swap.noIndexer')}
        </p>
      )}
      <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
        {t('swap.priceNote')}
      </p>
    </Card>
  );
}

type Dir = 'in' | 'out'; // in: SYN → sSYN (stake no-lock), out: sSYN → SYN (withdraw it)

/** What a conversion of `amount` delivers: the no-lock plan's fee (the 10% tax) comes off in either direction. */
export function conversionOut(amount: bigint, feeBps: bigint): { tax: bigint; received: bigint } {
  const received = afterExitFee(amount, feeBps);
  return { tax: amount - received, received };
}

/**
 * SYN ↔ sSYN: sSYN is the wallet's no-lock stake, so converting in stakes in the no-lock plan and converting out
 * withdraws from it; both pay that plan's fee (10% on the client's sheet), and nothing goes through the pool.
 */
function ConvertCard() {
  const { t } = useI18n();
  const { address } = useConnection();
  const { data: me } = usePosition(address);
  const { noLock } = useStakes(address);
  const { data: p } = useProtocol();
  const plan = p.plans?.[NO_LOCK];
  const [dir, setDir] = useState<Dir>('in');
  const [input, setInput] = useState('');
  const amount = parseAmount(input);
  const fromSym = dir === 'in' ? 'SYN' : 'sSYN';
  const toSym = dir === 'in' ? 'sSYN' : 'SYN';
  const balance = dir === 'in' ? me.syntra : noLock?.withdrawable ?? (address ? 0n : undefined);
  const feeBps = plan ? (dir === 'in' ? plan.stakeFeeBps : plan.unstakeFeeBps) : undefined;
  const { tax, received } = conversionOut(amount ?? 0n, feeBps ?? 0n);
  const all = amount !== null && balance !== undefined && amount === balance;
  const blocked = !plan
    ? t('common.loading')
    : amount === null || amount === 0n
      ? t('common.enterAmount')
      : balance !== undefined && amount > balance
        ? t('common.insufficient', { token: fromSym })
        : null;
  const request =
    !amount || !plan
      ? null
      : dir === 'in'
        ? { address: D.staking, abi: stakingManagerAbi, functionName: 'stake', args: [NO_LOCK, amount] }
        : noLock
          ? { address: D.staking, abi: stakingManagerAbi, functionName: 'withdraw', args: [BigInt(noLock.id), all ? 2n ** 256n - 1n : amount] }
          : null;
  const r = plan ? toNum(plan.paidRate) : NaN; // what the no-lock plan pays today, not its ceiling
  return (
    <Card highlight title={t('convert.title')}>
      <div className="field" style={{ gap: 12 }}>
        <AmountInput
          id="convert-in"
          label={t('convert.from', { token: fromSym })}
          value={input}
          onChange={setInput}
          token={fromSym}
          icon={dir === 'in' ? '/token-syntra.png' : '/token-ssyntra.png'}
          balance={fmtBalance(balance)}
          onMax={balance !== undefined && balance > 0n ? () => setInput(toInput(balance)) : undefined}
        />
        {/* the direction, turned around by the same flip button as the PancakeSwap form */}
        <button
          type="button"
          className="icon-btn"
          aria-label={t('convert.flip', { from: fromSym, to: toSym })}
          style={{ alignSelf: 'center' }}
          onClick={() => {
            setDir(dir === 'in' ? 'out' : 'in');
            setInput('');
          }}
        >
          <Icon name="swap" width={18} height={18} style={{ transform: 'rotate(90deg)' }} />
        </button>
        <div className="field">
          <div className="field-top">
            <span>{t('convert.to', { token: toSym })}</span>
          </div>
          <div className="amount" data-testid="convert-out">
            <span className="num" style={{ flex: 1, fontSize: 20, fontWeight: 600 }}>
              {amount ? fmtToken(received, 6) : '0.0'}
            </span>
            <span className="token-chip">
              <img src={dir === 'in' ? '/token-ssyntra.png' : '/token-syntra.png'} alt="" />
              {toSym}
            </span>
          </div>
        </div>
        <Rows
          items={[
            [t('convert.rate'), t('convert.rateValue', { pct: feeBps !== undefined ? fmtBps(feeBps) : '—' })],
            [t('convert.tax', { pct: feeBps !== undefined ? fmtBps(feeBps) : '—' }), amount ? `${fmtToken(tax, 6)} ${fromSym}` : '—'],
            [t('convert.earns'), plan ? t('convert.earnsValue', { apr: fmtPct(aprOf(r), 0), apy: fmtPct(apyOf(r), 1) }) : '—'],
          ]}
        />
        <Notice>{t('convert.about')}</Notice>
        {(() => {
          const action = (
            <ActionButton
              label={t(dir === 'in' ? 'convert.ctaIn' : 'convert.ctaOut')}
              blocked={blocked}
              approve={dir === 'in' && amount ? { token: D.syntra, spender: D.staking, amount, symbol: 'SYN' } : undefined}
              request={request as never}
              onDone={() => setInput('')}
            />
          );
          // SYN → sSYN is a no-lock stake: join the inviting referrer first (sSYN → SYN needs a stake, so a team)
          return dir === 'in' ? <JoinFirst>{action}</JoinFirst> : action;
        })()}
      </div>
    </Card>
  );
}

export function Swap() {
  const { t } = useI18n();
  const [mode, setMode] = useState<'swap' | 'convert'>('swap');
  const { address } = useConnection();
  const { data: me } = usePosition(address);
  const bnb = useBalance({ address, query: { enabled: Boolean(address) } });
  const pool = usePool();
  const now = useChainNow(30_000);
  const [from, setFrom] = useState<Sym>('USDT');
  const [to, setTo] = useState<Sym>('SYN');
  const [input, setInput] = useState('');
  const [slip, setSlip] = useState<'50' | '100' | '300'>('50');
  const amount = parseAmount(input);
  const path = pathFor(from, to, D);
  const choices: Sym[] = pool.bnbRoutable ? ['SYN', 'USDT', 'BNB'] : ['SYN', 'USDT'];
  const balanceOf = (s: Sym) => (s === 'SYN' ? me.syntra : s === 'USDT' ? me.usdt : bnb.data?.value);
  const tokenOptions = choices.map((c) => ({ value: c, icon: ICONS[c], name: c === 'BNB' ? t('swap.nativeCoin') : NAMES[c], balance: address ? fmtBalance(balanceOf(c)) : undefined }));

  const quote = useReadContract({
    address: D.router,
    abi: uniswapV2RouterAbi,
    functionName: 'getAmountsOut',
    args: [amount ?? 0n, path ?? []],
    query: { enabled: Boolean(path && amount && amount > 0n), refetchInterval: 10_000 },
  });
  const small = amount ? (amount / 10_000n > 0n ? amount / 10_000n : 1n) : 0n;
  const marginal = useReadContract({
    address: D.router,
    abi: uniswapV2RouterAbi,
    functionName: 'getAmountsOut',
    args: [small, path ?? []],
    query: { enabled: Boolean(path && small > 0n) },
  });
  const out = quote.data?.[quote.data.length - 1];
  const smallOut = marginal.data?.[marginal.data.length - 1];
  const impact = amount && out && smallOut ? priceImpact(amount, out, small, smallOut) : 0;
  const min = out ? withSlippage(out, BigInt(slip)) : 0n;
  const deadline = BigInt(Math.floor(now + 20 * 60));
  const balance = balanceOf(from);

  const pick = (side: 'from' | 'to', s: Sym) => {
    if (side === 'from') {
      if (s === to) setTo(from);
      setFrom(s);
    } else {
      if (s === from) setFrom(to);
      setTo(s);
    }
  };
  const blocked = !path
    ? t('swap.noRoute')
    : amount === null || amount === 0n
      ? t('common.enterAmount')
      : balance !== undefined && amount > balance
        ? t('common.insufficient', { token: from })
        : null;

  let request = null;
  if (path && amount && out) {
    if (from === 'BNB') {
      request = { address: D.router, abi: uniswapV2RouterAbi, functionName: 'swapExactETHForTokens', args: [min, path, address, deadline], value: amount };
    } else if (to === 'BNB') {
      request = { address: D.router, abi: uniswapV2RouterAbi, functionName: 'swapExactTokensForETH', args: [amount, min, path, address, deadline] };
    } else {
      request = { address: D.router, abi: uniswapV2RouterAbi, functionName: 'swapExactTokensForTokens', args: [amount, min, path, address, deadline] };
    }
  }
  const tokenOf = (s: Sym) => (s === 'SYN' ? D.syntra : D.usdt);
  const price = pool.rSyntra && pool.rUsdt ? toNum(pool.rUsdt) / toNum(pool.rSyntra) : NaN;

  return (
    <>
      <SectionHead
        eyebrow={t('swap.eyebrow')}
        title={t('swap.title')}
        right={
          <Segmented<'swap' | 'convert'>
            label={t('swap.mode')}
            value={mode}
            onChange={setMode}
            options={[
              { value: 'swap', label: t('swap.modeSwap') },
              { value: 'convert', label: 'SYN ↔ sSYN' },
            ]}
          />
        }
      />
      <div className="grid two form-layout">
        {mode === 'convert' ? <ConvertCard /> : (
        <Card highlight>
          <div className="field" style={{ gap: 12 }}>
            <AmountInput
              id="swap-in"
              label={t('swap.pay')}
              value={input}
              onChange={setInput}
              token={from}
              icon={ICONS[from]}
              balance={fmtBalance(balance)}
              onMax={balance !== undefined ? () => setInput(toInput(from === 'BNB' && balance > 10n ** 16n ? balance - 10n ** 16n : balance)) : undefined}
              chip={<TokenSelect<Sym> label={`${t('swap.pay')} · token`} value={from} onChange={(s) => pick('from', s)} options={tokenOptions} />}
            />
            <button
              type="button"
              className="icon-btn"
              aria-label={t('swap.flip')}
              style={{ alignSelf: 'center' }}
              onClick={() => {
                setFrom(to);
                setTo(from);
              }}
            >
              <Icon name="swap" width={18} height={18} style={{ transform: 'rotate(90deg)' }} />
            </button>
            <div className="field">
              <div className="field-top">
                <span>{t('swap.receive')}</span>
                <span>
                  {t('common.balance')}: <span className="num">{fmtBalance(balanceOf(to))}</span>
                </span>
              </div>
              <div className="amount" data-testid="swap-out">
                <span className="num" style={{ flex: 1, fontSize: 20, fontWeight: 600 }}>
                  {out ? fmtToken(out, 6) : '0.0'}
                </span>
                <TokenSelect<Sym> label={`${t('swap.receive')} · token`} value={to} onChange={(s) => pick('to', s)} options={tokenOptions} />
              </div>
            </div>
            <Rows
              items={[
                [t('swap.rate'), out && amount ? `1 ${from} = ${fmtNum(toNum(out) / toNum(amount), 6)} ${to}` : '—'],
                [t('swap.minReceived'), out ? `${fmtToken(min, 6)} ${to}` : '—'],
                [t('swap.impact'), <span key="i" className={impact > 0.03 ? 'neg' : ''}>{out ? fmtPct(impact) : '—'}</span>],
                [t('swap.route'), path ? [from, ...(path.length === 3 ? ['USDT'] : []), to].join(' → ') : '—'],
                [t('swap.fee'), '0.25% / hop'],
              ]}
            />
            <div className="row" style={{ alignItems: 'center' }}>
              <span className="soft" style={{ fontSize: 13 }}>
                {t('swap.slippage')}
              </span>
              <Segmented<'50' | '100' | '300'>
                label={t('swap.slippage')}
                value={slip}
                onChange={setSlip}
                options={[
                  { value: '50', label: '0.5%' },
                  { value: '100', label: '1%' },
                  { value: '300', label: '3%' },
                ]}
              />
            </div>
            {impact > 0.03 ? <Notice tone="warn">{t('swap.highImpact', { pct: fmtPct(impact) })}</Notice> : null}
            <ActionButton
              label={t('swap.cta')}
              blocked={blocked}
              approve={from !== 'BNB' && amount ? { token: tokenOf(from), spender: D.router, amount, symbol: from } : undefined}
              request={request as never}
              onDone={() => setInput('')}
            />
          </div>
        </Card>
        )}
        <div className="grid">
          <PriceCard />
        <Card title={t('swap.pool')}>
          <PoolStacks
            sides={[
              { token: 'SYN', usd: pool.rSyntra !== undefined && Number.isFinite(price) ? toNum(pool.rSyntra) * price : NaN, label: `${fmtToken(pool.rSyntra, 0)} SYN` },
              { token: 'USDT', usd: pool.rUsdt !== undefined ? toNum(pool.rUsdt) : NaN, label: `${fmtToken(pool.rUsdt, 0)} USDT` },
            ]}
          />
          <Rows
            items={[
              [t('swap.reserves'), `${fmtToken(pool.rSyntra, 2)} SYN · ${fmtToken(pool.rUsdt, 2)} USDT`],
              ['SYN', Number.isFinite(price) ? fmtUsd(price) : '—'],
              [t('swap.tvl'), pool.rUsdt !== undefined ? fmtUsd(2 * toNum(pool.rUsdt)) : '—'],
            ]}
          />
          <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
            {t('swap.noTax')}
          </p>
        </Card>
        </div>
      </div>
    </>
  );
}
